import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import HomeRedirect from './components/HomeRedirect';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import PropertyDetail from './pages/PropertyDetail';
import UploadDocument from './pages/UploadDocument';
import ThreadsInbox from './pages/ThreadsInbox';
import DataHub from './pages/DataHub';
import MasterPropertyDetail from './pages/MasterPropertyDetail';
import Settings from './pages/Settings';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import Deals from './pages/Deals';
import Renewals from './pages/Renewals';
import DealDetail from './pages/DealDetail';
import DocumentGenerator from './pages/DocumentGenerator';
import DocumentLibrary from './pages/DocumentLibrary';
import CurrentListings from './pages/CurrentListings';
import ListingSites from './pages/ListingSites';
import PublicListing from './pages/PublicListing';
import Prospecting from './pages/Prospecting';
import Campaigns from './pages/Campaigns';
import Reports from './pages/Reports';
import PublicDealRoom from './pages/PublicDealRoom';
import PublicEscrowSummary from './pages/PublicEscrowSummary';
import Playbooks from './pages/Playbooks';
import Syndication from './pages/Syndication';
import AssetServices from './pages/AssetServices';
import VendorDirectory from './pages/VendorDirectory';
import ManagedPropertyDetailPage from './pages/ManagedPropertyDetail';
import AIConversations from './pages/AIConversations';
import OwnerOutreach from './pages/OwnerOutreach';
import MarketIntel from './pages/MarketIntel';
import DataHealth from './pages/DataHealth';
import CompSearch from './pages/CompSearch';
import CountyTransfers from './pages/CountyTransfers';
import PermitMonitor from './pages/PermitMonitor';
import CRMIntelligence from './pages/CRMIntelligence';
import NewsIntelligence from './pages/NewsIntelligence';
import SignalRadar from './pages/SignalRadar';
import DealRadar from './pages/DealRadar';
import LandUpside from './pages/LandUpside';
import ZoningRules from './pages/ZoningRules';
import GHLSync from './pages/GHLSync';
import Opportunities from './pages/Opportunities';
import OpportunityDetail from './pages/OpportunityDetail';
import Conversations from './pages/Conversations';
import ConversationDetail from './pages/ConversationDetail';
import NeedsAttention from './pages/NeedsAttention';
import Todos from './pages/Todos';
import Morning from './pages/Morning';
import Leads from './pages/Leads';
import CampaignTracker from './pages/CampaignTracker';
import StreetAnalysis from './pages/StreetAnalysis';
import ATTOMEnrich from './pages/ATTOMEnrich';
import ProspectingMap from './pages/ProspectingMap';
import PropertyImport from './pages/PropertyImport';
import ClientPortal from './pages/ClientPortal';
import PortalsAdmin from './pages/PortalsAdmin';
import ChangeQueue from './pages/ChangeQueue';
import ContractBuilder from './pages/ContractBuilder';
import Tours from './pages/Tours';
import StartTour from './pages/StartTour';
import TourCapture from './pages/TourCapture';
import TourReport from './pages/TourReport';
import HostGate from './components/HostGate';
import { PageSubjectProvider } from './contexts/PageSubjectContext';
import { PageActionProvider } from './contexts/PageActionContext';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.id === 'anonymous') return <Navigate to="/login" />;
  return <>{children}</>;
}

/**
 * ContractBuilder itself takes plain props (dealId, formCode) rather than reading the route
 * directly, which is what lets its test render it with no Router at all. This wrapper is the
 * only place that reads the URL. formCode is not part of the route (D26 v1 maps only forms 64
 * and 72 - Task 9); until there is a form picker, it comes from `?formCode=`.
 *
 * No default. Form 64 is the Multi-Tenant Lease, 72 is a Purchase Agreement - defaulting either
 * way would let a bare `/contracts/<dealId>` link (nothing links here yet) silently build the
 * wrong instrument for a sale deal. Absent `?formCode=`, ContractBuilder itself renders a prompt
 * asking which form and fetches nothing.
 */
function ContractBuilderRoute() {
  const { dealId } = useParams<{ dealId: string }>();
  const [searchParams] = useSearchParams();
  if (!dealId) return null;
  return <ContractBuilder dealId={dealId} formCode={searchParams.get('formCode')} />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/listing/:slug" element={<PublicListing />} />
      <Route path="/deal-room/:token" element={<PublicDealRoom />} />
      <Route path="/escrow/:token" element={<PublicEscrowSummary />} />
      <Route path="/portal/:token" element={<ClientPortal />} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/upload" element={<PrivateRoute><UploadDocument /></PrivateRoute>} />
      <Route path="/threads" element={<PrivateRoute><ThreadsInbox /></PrivateRoute>} />
      <Route path="/properties/:id" element={<PrivateRoute><PropertyDetail /></PrivateRoute>} />
      <Route path="/data-hub" element={<PrivateRoute><DataHub /></PrivateRoute>} />
      <Route path="/data-hub/:id" element={<PrivateRoute><MasterPropertyDetail /></PrivateRoute>} />
      {/* WT1 walk-throughs. The capture screen renders WITHOUT Layout (TourCapture.tsx says why). */}
      <Route path="/tours" element={<PrivateRoute><Tours /></PrivateRoute>} />
      <Route path="/tours/new" element={<PrivateRoute><StartTour /></PrivateRoute>} />
      <Route path="/tour/:tourId" element={<PrivateRoute><TourCapture /></PrivateRoute>} />
      <Route path="/tour/:tourId/report" element={<PrivateRoute><TourReport /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/crm/contacts" element={<PrivateRoute><Contacts /></PrivateRoute>} />
      <Route path="/crm/contacts/:id" element={<PrivateRoute><ContactDetail /></PrivateRoute>} />
      <Route path="/crm/companies" element={<PrivateRoute><Companies /></PrivateRoute>} />
      <Route path="/crm/companies/:id" element={<PrivateRoute><CompanyDetail /></PrivateRoute>} />
      <Route path="/crm/deals" element={<PrivateRoute><Deals /></PrivateRoute>} />
      <Route path="/crm/deals/:id" element={<PrivateRoute><DealDetail /></PrivateRoute>} />
      <Route path="/crm/renewals" element={<PrivateRoute><Renewals /></PrivateRoute>} />
      <Route path="/crm/opportunities" element={<PrivateRoute><Opportunities /></PrivateRoute>} />
      <Route path="/crm/opportunities/:id" element={<PrivateRoute><OpportunityDetail /></PrivateRoute>} />
      <Route path="/crm/conversations" element={<PrivateRoute><Conversations /></PrivateRoute>} />
      <Route path="/crm/conversations/:id" element={<PrivateRoute><ConversationDetail /></PrivateRoute>} />
      <Route path="/morning" element={<PrivateRoute><Morning /></PrivateRoute>} />
      <Route path="/leads" element={<PrivateRoute><Leads /></PrivateRoute>} />
      <Route path="/campaign-tracker" element={<PrivateRoute><CampaignTracker /></PrivateRoute>} />
      <Route path="/todos" element={<PrivateRoute><Todos /></PrivateRoute>} />
      <Route path="/crm/needs-attention" element={<PrivateRoute><NeedsAttention /></PrivateRoute>} />
      {/* DOC1: /documents is the LIBRARY. The generator keeps its own path - the nav's
          "Documents" used to open it, which is the trap BROWSER-VERIFICATION.md records. */}
      <Route path="/documents" element={<PrivateRoute><DocumentLibrary /></PrivateRoute>} />
      <Route path="/documents/generate" element={<PrivateRoute><DocumentGenerator /></PrivateRoute>} />
      <Route path="/current-listings" element={<PrivateRoute><CurrentListings /></PrivateRoute>} />
      <Route path="/listing-sites" element={<PrivateRoute><ListingSites /></PrivateRoute>} />
      <Route path="/prospecting" element={<PrivateRoute><Prospecting /></PrivateRoute>} />
      <Route path="/campaigns" element={<PrivateRoute><Campaigns /></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
      <Route path="/playbooks" element={<PrivateRoute><Playbooks /></PrivateRoute>} />
      <Route path="/syndication" element={<PrivateRoute><Syndication /></PrivateRoute>} />
      <Route path="/asset-services" element={<PrivateRoute><AssetServices /></PrivateRoute>} />
      <Route path="/asset-services/vendors" element={<PrivateRoute><VendorDirectory /></PrivateRoute>} />
      <Route path="/asset-services/:id" element={<PrivateRoute><ManagedPropertyDetailPage /></PrivateRoute>} />
      <Route path="/ai" element={<PrivateRoute><AIConversations /></PrivateRoute>} />
      {/* The rail is on every page now, so a dedicated chat route has nothing to be. Redirected
          rather than removed: this URL is in the nav history and possibly a bookmark. */}
      <Route path="/ai/assistant" element={<Navigate to="/ai" replace />} />
      <Route path="/ai/outreach" element={<PrivateRoute><OwnerOutreach /></PrivateRoute>} />
      <Route path="/comp-search" element={<PrivateRoute><CompSearch /></PrivateRoute>} />
      <Route path="/market-intel" element={<PrivateRoute><MarketIntel /></PrivateRoute>} />
      <Route path="/data-health" element={<PrivateRoute><DataHealth /></PrivateRoute>} />
      <Route path="/county-transfers" element={<PrivateRoute><CountyTransfers /></PrivateRoute>} />
      <Route path="/permits" element={<PrivateRoute><PermitMonitor /></PrivateRoute>} />
      <Route path="/crm-intelligence" element={<PrivateRoute><CRMIntelligence /></PrivateRoute>} />
      <Route path="/news-intel" element={<PrivateRoute><NewsIntelligence /></PrivateRoute>} />
      <Route path="/deal-radar" element={<PrivateRoute><DealRadar /></PrivateRoute>} />
      <Route path="/land-upside" element={<PrivateRoute><LandUpside /></PrivateRoute>} />
      <Route path="/signal-radar" element={<PrivateRoute><SignalRadar /></PrivateRoute>} />
      <Route path="/zoning-rules" element={<PrivateRoute><ZoningRules /></PrivateRoute>} />
      <Route path="/ghl-sync" element={<PrivateRoute><GHLSync /></PrivateRoute>} />
      <Route path="/street-analysis" element={<PrivateRoute><StreetAnalysis /></PrivateRoute>} />
      <Route path="/attom" element={<PrivateRoute><ATTOMEnrich /></PrivateRoute>} />
      <Route path="/prospecting-map" element={<PrivateRoute><ProspectingMap /></PrivateRoute>} />
      <Route path="/property-import" element={<PrivateRoute><PropertyImport /></PrivateRoute>} />
      <Route path="/portals" element={<PrivateRoute><PortalsAdmin /></PrivateRoute>} />
      <Route path="/change-queue" element={<PrivateRoute><ChangeQueue /></PrivateRoute>} />
      <Route path="/contracts/:dealId" element={<PrivateRoute><ContractBuilderRoute /></PrivateRoute>} />
      <Route path="/" element={<HomeRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    /*
      THE HOST GATE IS THE OUTERMOST THING IN THE APP, and that placement is the point: on a gated
      client host a staff path must leave for `app.apex-res.com` BEFORE auth mounts and checks a
      session against an origin the visitor is about to leave. The policy itself lives in ONE file,
      `lib/hostPolicy.ts` - do not restate any part of it here.
    */
    <HostGate>
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          {/*
            🚨 THE PROVIDER MUST BE ABOVE THE PAGES, AND IT IS THE PAGES THAT RENDER Layout.

            It lived in Layout.tsx and the whole rail was inert - one "Assistant" tab, header
            reading "General", no Thread tab, on a deal page that rendered perfectly. Found by
            opening the app and looking; all 495 tests were green.

            A page is not Layout's child in the React tree. DealDetail calls
            useSetPageSubject(dealSubject) in its OWN body and then RENDERS <Layout>, so Layout
            is the page's CHILD. A hook reads the context that exists ABOVE its own component,
            so a provider inside Layout is BELOW every publisher: useSetPageSubject got
            createContext's default no-op publish and nothing was ever published, while
            usePageSubject inside the rail (which IS below Layout's provider) always read null.

            One instance for the whole app, inside BrowserRouter and AuthProvider so pages keep
            the router and auth they need, and outside every <Route> so a navigation does not
            remount it and drop the subject.
          */}
          <PageSubjectProvider>
            {/* The dialogs a page offers the AI rail. Mounted beside the subject provider and
                for the same reason: both are read by the rail inside Layout and written by the
                record pages below it, so both must sit ABOVE Layout or the writes and the reads
                never meet. */}
            <PageActionProvider>
              <AppRoutes />
            </PageActionProvider>
          </PageSubjectProvider>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
    </HostGate>
  );
}
